import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowBack, Edit } from "@mui/icons-material";
import { useInvestigator } from "@/api/hooks/investigators";
import {
  DEBARMENT_STATUS_LABELS,
  INVESTIGATOR_ROLE_LABELS,
} from "@/api/labels";
import DocumentsSection from "@/components/documents/DocumentsSection";
import { formatDate, formatDateTime } from "@/utils/format";
import {
  EXPIRY_STATE_CHIP_COLOR,
  classifyExpiry,
} from "@/utils/trainingExpiry";

function LabeledValue({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

function ExpiryRow({
  label,
  iso,
}: {
  label: string;
  iso: string | null;
}) {
  const state = classifyExpiry(iso);
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <Typography variant="body2">
        {label}: {iso ? formatDate(iso) : "—"}
      </Typography>
      {iso && (
        <Chip
          size="small"
          label={state}
          color={EXPIRY_STATE_CHIP_COLOR[state]}
          variant="outlined"
        />
      )}
    </Stack>
  );
}

export default function InvestigatorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: inv, isLoading, error } = useInvestigator(id);

  if (isLoading) return <CircularProgress />;
  if (error || !inv)
    return (
      <Alert severity="error">{error?.message ?? "Investigator not found."}</Alert>
    );

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton
          onClick={() => navigate("/clinops/sites/investigators")}
          aria-label="Back"
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5">
            {inv.first_name} {inv.last_name}
            {!inv.is_active && (
              <Chip
                label="Inactive"
                size="small"
                color="default"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            NPI: {inv.npi ?? "—"}
          </Typography>
        </Box>
        <Button
          startIcon={<Edit />}
          onClick={() => navigate(`/clinops/sites/investigators/${inv.id}/edit`)}
        >
          Edit
        </Button>
      </Box>

      <Box sx={{ display: "grid", gap: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Identity
            </Typography>
            <Grid container spacing={3} sx={{ mt: 0 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledValue label="Email">{inv.email ?? "—"}</LabeledValue>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledValue label="Phone">{inv.phone ?? "—"}</LabeledValue>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledValue label="Created">
                  {formatDateTime(inv.created_at)}
                </LabeledValue>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledValue label="Updated">
                  {formatDateTime(inv.updated_at)}
                </LabeledValue>
              </Grid>
              {inv.notes && (
                <Grid size={{ xs: 12 }}>
                  <LabeledValue label="Notes">
                    <span style={{ whiteSpace: "pre-wrap" }}>{inv.notes}</span>
                  </LabeledValue>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Credentialing
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <ExpiryRow label="GCP training" iso={inv.gcp_training_expires_at} />
              <ExpiryRow
                label="HIPAA training"
                iso={inv.hipaa_training_expires_at}
              />
              <Divider flexItem />
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography variant="body2">Debarment status:</Typography>
                <Chip
                  size="small"
                  label={DEBARMENT_STATUS_LABELS[inv.debarment_status]}
                  color={
                    inv.debarment_status === "debarred"
                      ? "error"
                      : inv.debarment_status === "clear"
                        ? "success"
                        : "default"
                  }
                />
                {inv.debarment_checked_at && (
                  <Typography variant="caption" color="text.secondary">
                    (checked {formatDate(inv.debarment_checked_at)})
                  </Typography>
                )}
              </Stack>
              {inv.board_certifications && (
                <LabeledValue label="Board certifications">
                  <span style={{ whiteSpace: "pre-wrap" }}>
                    {inv.board_certifications}
                  </span>
                </LabeledValue>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="overline" color="text.secondary">
                Active studies
              </Typography>
              <Chip
                size="small"
                label={`${inv.active_participations.length} total`}
              />
            </Box>
            <Stack divider={<Divider flexItem />} spacing={1}>
              {inv.active_participations.map((a) => (
                <Box
                  key={a.participation_id}
                  sx={{ display: "flex", gap: 2, py: 1 }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Link
                      to={`/clinops/sites/participations/${a.participation_id}`}
                      style={{ textDecoration: "none" }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "primary.main" }}
                      >
                        {a.study_name}
                      </Typography>
                    </Link>
                    <Typography variant="caption" color="text.secondary">
                      {a.site_name} · {a.location_name} · assigned{" "}
                      {formatDate(a.assigned_at)}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={INVESTIGATOR_ROLE_LABELS[a.role]}
                    color={a.role === "pi" ? "primary" : "default"}
                    variant="outlined"
                  />
                </Box>
              ))}
              {inv.active_participations.length === 0 && (
                <Typography color="text.secondary" sx={{ py: 1 }}>
                  No active study assignments.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        <DocumentsSection entityType="investigator" entityId={inv.id} />
      </Box>
    </Box>
  );
}
